import SkeletonPose from "./skeleton_pose";
import AnimationClip, { AnimationChannel } from "./animation_clip";

export default class AnimationPoseSampler {
  static sampleClipIntoPose(
    clip: AnimationClip,
    time: number,
    pose: SkeletonPose,
    loop: boolean
  ): void {
    for (const channel of clip.channels) {
      const target = pose.localPoses[channel.nodeIndex];
      if (!target) continue;

      const sample = AnimationPoseSampler.sampleChannel(
        channel,
        time,
        clip.duration,
        loop
      );
      if (!sample) continue;

      switch (channel.path) {
        case "translation":
          target.translation.set(sample);
          break;
        case "rotation":
          target.rotation.set(sample);
          AnimationPoseSampler.normalizeQuat(target.rotation);
          break;
        case "scale":
          target.scale.set(sample);
          break;
      }
    }
  }

  static sampleChannel(
    channel: AnimationChannel,
    time: number,
    clipDuration: number,
    loop: boolean
  ): Float32Array | null {
    const times = channel.times;
    const values = channel.values;

    if (times.length === 0) return null;

    const components =
      channel.path === "rotation" ? 4 :
      channel.path === "translation" ? 3 :
      channel.path === "scale" ? 3 : 0;

    if (components === 0) return null;

    if (times.length === 1) {
      return values.slice(0, components);
    }

    let t = time;

    if (loop && clipDuration > 0) {
      t = t % clipDuration;
      if (t < 0) t += clipDuration;
    }

    const firstTime = times[0];
    const lastIndex = times.length - 1;
    const lastTime = times[lastIndex];

    if (!loop) {
      if (t <= firstTime) {
        return values.slice(0, components);
      }

      if (t >= lastTime) {
        const start = lastIndex * components;
        return values.slice(start, start + components);
      }

      for (let i = 0; i < lastIndex; i++) {
        const t0 = times[i];
        const t1 = times[i + 1];

        if (t >= t0 && t <= t1) {
          return AnimationPoseSampler.interpolateChannelSamples(
            channel,
            i,
            i + 1,
            t,
            t0,
            t1
          );
        }
      }

      const start = lastIndex * components;
      return values.slice(start, start + components);
    }

    for (let i = 0; i < lastIndex; i++) {
      const t0 = times[i];
      const t1 = times[i + 1];

      if (t >= t0 && t <= t1) {
        return AnimationPoseSampler.interpolateChannelSamples(
          channel,
          i,
          i + 1,
          t,
          t0,
          t1
        );
      }
    }

    const seamT0 = lastTime;
    const seamT1 = firstTime + clipDuration;
    const seamT = t < firstTime ? t + clipDuration : t;

    return AnimationPoseSampler.interpolateChannelSamples(
      channel,
      lastIndex,
      0,
      seamT,
      seamT0,
      seamT1
    );
  }

  private static interpolateChannelSamples(
    channel: AnimationChannel,
    index0: number,
    index1: number,
    time: number,
    t0: number,
    t1: number
  ): Float32Array {
    const values = channel.values;

    const components =
      channel.path === "rotation" ? 4 :
      channel.path === "translation" ? 3 :
      channel.path === "scale" ? 3 : 0;

    const start0 = index0 * components;
    const start1 = index1 * components;

    if (channel.interpolation === "STEP" || t1 <= t0) {
      return values.slice(start0, start0 + components);
    }

    const alpha = (time - t0) / (t1 - t0);

    if (channel.path === "rotation") {
      const a = new Float32Array([
        values[start0 + 0],
        values[start0 + 1],
        values[start0 + 2],
        values[start0 + 3],
      ]);

      const b = new Float32Array([
        values[start1 + 0],
        values[start1 + 1],
        values[start1 + 2],
        values[start1 + 3],
      ]);

      return AnimationPoseSampler.nlerpQuatShortest(a, b, alpha);
    }

    const out = new Float32Array(components);
    for (let c = 0; c < components; c++) {
      out[c] = values[start0 + c] * (1 - alpha) + values[start1 + c] * alpha;
    }

    return out;
  }

  static normalizeQuat(q: Float32Array): void {
    const len = Math.hypot(q[0], q[1], q[2], q[3]);
    if (len > 0.000001) {
      q[0] /= len;
      q[1] /= len;
      q[2] /= len;
      q[3] /= len;
    }
  }

  static lerpVec3(a: Float32Array, b: Float32Array, t: number): Float32Array {
    return new Float32Array([
      a[0] * (1 - t) + b[0] * t,
      a[1] * (1 - t) + b[1] * t,
      a[2] * (1 - t) + b[2] * t,
    ]);
  }

  static nlerpQuatShortest(a: Float32Array, b: Float32Array, t: number): Float32Array {
    let bx = b[0];
    let by = b[1];
    let bz = b[2];
    let bw = b[3];

    const dot = a[0] * bx + a[1] * by + a[2] * bz + a[3] * bw;

    if (dot < 0) {
      bx = -bx;
      by = -by;
      bz = -bz;
      bw = -bw;
    }

    const out = new Float32Array([
      a[0] * (1 - t) + bx * t,
      a[1] * (1 - t) + by * t,
      a[2] * (1 - t) + bz * t,
      a[3] * (1 - t) + bw * t,
    ]);

    AnimationPoseSampler.normalizeQuat(out);
    return out;
  }
}
